import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfileQuery, useUpdateProfileMutation } from '@entities/Profile';
import { useAuthStore } from '@entities/User';
import { trackEvent } from '@shared/analytics';
import { BellIcon, toast } from '@shared/ui';

// Закрытие запоминается в этом браузере (как подсказка о готовых модулях): плашка — предложение, а не требование.
const HIDE_KEY = 'lx.hint.reminders';

function readHidden(): boolean {
  try {
    return window.localStorage.getItem(HIDE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Напоминания выключены — одна плашка с кнопкой «Включить» (новая главная, решение владельца 2026-10-22): письмо
 * в час напоминаний — внешний повод вернуться, самый сильный рычаг удержания (разбор 2026-10-21). Включены — плашки нет.
 */
export function ReminderBanner() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const patchUser = useAuthStore((s) => s.patchUser);
  const profile = useProfileQuery().data;
  const save = useUpdateProfileMutation();
  const [hidden, setHidden] = useState(readHidden);

  if (!user || user.notifications !== false || hidden) return null;
  const hour = profile?.reminder_hour ?? 19;

  const enable = () => {
    if (save.isPending) return;
    save.mutate(
      { is_notification: true },
      {
        onSuccess: () => {
          patchUser({ notifications: true });
          trackEvent('reminder_toggle_changed', { enabled: true, place: 'dashboard' });
          toast({ tone: 'success', message: t('dashboard.home.reminder.saved', { hour }) });
        },
        onError: () => toast({ tone: 'error', message: t('dashboard.home.reminder.error') }),
      },
    );
  };
  const later = () => {
    try {
      window.localStorage.setItem(HIDE_KEY, '1');
    } catch {
      // хранилище недоступно — плашка просто покажется снова
    }
    setHidden(true);
  };

  return (
    <section className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-[18px] border border-solid border-accent/30 bg-card px-4 py-3.5">
      <span aria-hidden className="grid h-9 w-9 flex-none place-items-center rounded-full bg-accent/15 text-accent">
        <BellIcon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 basis-[220px]">
        <span className="block text-[15px] font-semibold leading-5 text-fg">{t('dashboard.home.reminder.title')}</span>
        <span className="mt-0.5 block text-[13px] leading-[18px] text-muted">{t('dashboard.home.reminder.text', { hour })}</span>
      </span>
      <span className="flex flex-none items-center gap-2">
        <button
          type="button"
          onClick={enable}
          disabled={save.isPending}
          className="h-10 rounded-pill bg-accent px-4 text-[14px] font-semibold text-fg-inv transition-colors hover:bg-accent-hi disabled:opacity-60"
        >
          {t('dashboard.home.reminder.on')}
        </button>
        <button type="button" onClick={later} className="h-10 px-2 text-[14px] font-medium text-muted hover:text-fg">
          {t('dashboard.home.reminder.later')}
        </button>
      </span>
    </section>
  );
}
